import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";

/**
 * Full-screen overlay shown while an AI analysis is running.
 *
 * Why this exists: Claude analyses take 25-60 seconds. A static "Analyzing…"
 * button looks frozen and users either bail or refresh (losing their work).
 * This component does three things to reassure them:
 *  - cycles through honest, friendly status messages every ~5 seconds
 *  - shows a smoothly-animating progress bar that asymptotes toward but never
 *    reaches 100% (so it never lies — when the API actually returns, the
 *    parent component unmounts this overlay)
 *  - shows elapsed time so power users have an honest signal
 */
const STAGES = [
  { at: 0,  label: "Pulling your data…",                   tip: "Reading the metrics you picked from Google Analytics." },
  { at: 6,  label: "Cleaning up the numbers…",             tip: "Comparing periods, calculating deltas, spotting outliers." },
  { at: 14, label: "Asking Claude what it means…",         tip: "Our AI is reading the data the way a real analyst would." },
  { at: 30, label: "Writing your plain-English report…",   tip: "This part takes the longest — usually 15-30 seconds." },
  { at: 50, label: "Polishing the language…",              tip: "Almost done. Hang tight." },
  { at: 80, label: "Just about there…",                    tip: "Bigger date ranges and richer data take a little longer." },
];

function pickStage(seconds) {
  let current = STAGES[0];
  for (const s of STAGES) {
    if (seconds >= s.at) current = s;
  }
  return current;
}

export default function AnalysisRunning({ label = "Analyzing your data" }) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Progress asymptotes toward 95% — we never claim "done" until the parent
  // gets a response and unmounts us.
  const progress = Math.min(95, Math.round(100 * (1 - Math.exp(-seconds / 25))));
  const stage = pickStage(seconds);

  return (
    <div
      className="fixed inset-0 z-50 bg-[#F7F5F0]/95 backdrop-blur-sm flex items-center justify-center px-6"
      role="status"
      aria-live="polite"
      data-testid="analysis-running-overlay"
    >
      <div className="w-full max-w-md rounded-3xl bg-white border border-black/5 p-8 md:p-10 bento-shadow text-center">
        {/* Animated icon */}
        <div className="relative w-16 h-16 mx-auto mb-6">
          <div className="absolute inset-0 rounded-2xl bg-[#E27D60]/15 animate-ping" />
          <div className="relative w-16 h-16 rounded-2xl bg-[#1E392A] grid place-items-center">
            <Sparkles className="w-7 h-7 text-[#E27D60] animate-pulse" />
          </div>
        </div>

        <div className="tag-label text-[#E27D60]">{label}</div>
        <div
          className="font-display text-2xl md:text-3xl mt-2 leading-tight transition-opacity duration-500"
          data-testid="analysis-running-stage"
          key={stage.label /* re-mount triggers fade */}
        >
          {stage.label}
        </div>
        <p className="text-sm text-[#1E392A]/65 mt-3 leading-relaxed">{stage.tip}</p>

        {/* Progress bar */}
        <div className="mt-7 h-2 w-full rounded-full bg-[#F7F5F0] overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#E27D60] to-[#1E392A] transition-[width] duration-1000 ease-out"
            style={{ width: `${progress}%` }}
            data-testid="analysis-running-progress"
          />
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-[#1E392A]/55">
          <span data-testid="analysis-running-elapsed">{seconds}s elapsed</span>
          <span>Most reports finish in 30-45s</span>
        </div>

        <p className="text-xs text-[#1E392A]/45 mt-6 leading-relaxed">
          Please don't refresh or close this tab — your report will appear here automatically.
        </p>
      </div>
    </div>
  );
}
